import React, { useState } from 'react';
import { TRACK_EVENT, Typography } from '@tkww/the-bash-frontend';

import EditorialContainer from 'components/grid/EditorialContainer';
import { HeroWithConfetti } from 'components/Hero';
import ContactForm from 'views/ContactPage/ContactForm';
import FormSent from 'views/ContactPage/FormSent';
import { sendContactUs } from 'api/contactUs';
import {
  BLOG_PAGE_LINK,
  HERO_SUBTITLE,
  HERO_TITLE,
  SUBJECTS_BY_ID,
  TRACK_NAME_CLICK_AREA,
  TRACK_NAME_CTA,
  TRACK_NAME_SUBMIT,
  TRACK_NAME_VALIDATION,
} from 'views/ContactPage/constants';
import { useDispatch } from 'react-redux';
import { gmConfig } from 'helpers/config';

const ContactPageView = () => {
  const [ticketNumber, setTicketNumber] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    whoAmI: '',
    subject: 0,
    message: '',
    gigId: '',
    memberId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const HELP_PAGE_LINK = `https://${gmConfig.domains.www}/help`;

  const trackValidationErrors = (errors) => {
    const validatedFields = [];
    const validatedMessages = [];

    if (!errors) {
      return;
    }

    Object.keys(errors).forEach((k) => {
      validatedFields.push(k);
      validatedMessages.push(errors[k]);
    });

    dispatch({
      type: TRACK_EVENT,
      name: TRACK_NAME_VALIDATION,
      properties: {
        validatedFields,
        validatedMessages,
        clickArea: TRACK_NAME_CLICK_AREA,
      },
    });
  };

  const trackCtaClick = (clickTarget) => {
    dispatch({
      type: TRACK_EVENT,
      name: TRACK_NAME_CTA,
      properties: {
        clickTarget,
        clickArea: TRACK_NAME_CLICK_AREA,
      },
    });
  };

  const trackSuccessfulSubmit = (subject, userType, wasGigIDProvided, wasMemberIdProvided) => {
    dispatch({
      type: TRACK_EVENT,
      name: TRACK_NAME_SUBMIT,
      properties: {
        subject,
        userType,
        gigIDProvided: wasGigIDProvided,
        memberIdProvided: wasMemberIdProvided,
      },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const data = {
        ...formData,
        emailConfirm: formData.email,
        subject: SUBJECTS_BY_ID[formData.subject] || {},
      };
      const { id: ticketId } = await sendContactUs({
        ...formData,
        emailConfirm: formData.email,
        subject: SUBJECTS_BY_ID[formData.subject] || {},
      });

      trackSuccessfulSubmit(data.subject.name, data.whoAmI, !!data.gigId, !!data.memberId);
      setTicketNumber(ticketId);
      window.scrollTo(0, 0);
    } catch (error) {
      const { errors } = error;
      if (errors) {
        const fieldErrors = errors.reduce((acc, current) => {
          const { name, message } = current;
          const field = name.replace('ticketViewModel.', '');
          if (!acc[field]) {
            return {
              ...acc,
              [field]: message,
            };
          }

          return acc;
        }, {});
        setFormErrors(fieldErrors);
        trackValidationErrors(fieldErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <HeroWithConfetti title={HERO_TITLE} subtitle={HERO_SUBTITLE} />
      <EditorialContainer justifyContent="center" sx={{ marginTop: 4, marginBottom: 8 }}>
        {ticketNumber ? (
          <FormSent ticketNumber={ticketNumber} />
        ) : (
          <>
            <Typography variant="body1" mb={2}>
              Contact us by filling out the form below and a representative from our Account
              Services team will get back to you within 1 business day. You can also give us a call
              at 866-342-9794.
            </Typography>
            <Typography variant="body1">
              If you’re a party planner, check out our{' '}
              <a href={HELP_PAGE_LINK} target="__blank" onClick={() => trackCtaClick('our FAQS')}>
                FAQs
              </a>{' '}
              for more information. If you’re a vendor, you can find more information and resources
              in{' '}
              <a href={BLOG_PAGE_LINK} target="__blank" onClick={() => trackCtaClick('our blog')}>
                our blog
              </a>
              .
            </Typography>
            <ContactForm
              data={formData}
              errors={formErrors}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </>
        )}
      </EditorialContainer>
    </>
  );
};

export default ContactPageView;
